import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, ArrowLeft, Sparkles, ShieldCheck, Building2 } from "lucide-react";
import { getComparisonRecommendation, trackComparisonClick } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import ProviderGrid from "@/components/ProviderGrid";

const PRIORITIES = [
  { id: "prijs", label: "Laagste prijs", desc: "Goedkoopste tarief" },
  { id: "groen", label: "100% groen", desc: "Nederlandse groene stroom" },
  { id: "zekerheid", label: "Zekerheid", desc: "Vaste prijs, geen verrassingen" },
  { id: "service", label: "Service", desc: "Goede klantenservice & app" },
];

export default function Compare() {
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | result
  const [kwh, setKwh] = useState("");
  const [postcode, setPostcode] = useState("");
  const [hasSolar, setHasSolar] = useState(false);
  const [contractType, setContractType] = useState("");
  const [priority, setPriority] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await getComparisonRecommendation({
        yearly_consumption_kwh: kwh ? parseFloat(kwh) : null,
        has_solar: hasSolar,
        contract_type: contractType || null,
        priority: priority || null,
      });
      setResult(r);
      setStep("result");
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Kon de vergelijking niet laden. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  const handleProviderClick = async (providerId) => {
    try {
      const r = await trackComparisonClick({ provider_id: providerId });
      if (r.url) window.open(r.url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Doorsturen mislukt — probeer opnieuw.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="compare-logo-link">
            <div className="h-9 w-9 rounded-xl bg-blue-700 text-white grid place-items-center shadow-sm">
              <Zap className="h-5 w-5" />
            </div>
            <span className="font-heading font-bold text-base sm:text-lg text-slate-900">EnergieKontract</span>
          </Link>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-slate-600 hover:text-blue-700 flex items-center gap-1.5 transition-colors"
            data-testid="compare-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Terug
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14">
        {step === "form" && (
          <section data-testid="compare-form-section">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
                <Sparkles className="h-3.5 w-3.5" />
                Direct vergelijken
              </span>
              <h1 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
                De beste energieleverancier
                <br />
                <span className="text-blue-700">in 3 vragen</span>
              </h1>
              <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
                Geen contract om te uploaden? Geen probleem. Vul je verbruik in en ontdek welke aanbieder het beste bij jou past.
              </p>
              <Link
                to="/zakelijk"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-4 py-1.5 transition-all"
                data-testid="compare-b2b-link"
              >
                <Building2 className="h-3.5 w-3.5" />
                Bedrijf of ZZP? Bekijk zakelijke energie →
              </Link>
            </div>

            <form
              onSubmit={submit}
              className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.08)]"
              data-testid="compare-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="kwh" className="text-sm font-semibold text-slate-800 mb-2 block">
                    Jaarverbruik (kWh)
                  </Label>
                  <Input
                    id="kwh"
                    type="number"
                    inputMode="numeric"
                    value={kwh}
                    onChange={(e) => setKwh(e.target.value)}
                    placeholder="bv. 3000"
                    className="h-12"
                    data-testid="compare-kwh-input"
                  />
                  <p className="mt-1.5 text-xs text-slate-500">Gemiddeld huishouden: 2.700 kWh</p>
                </div>
                <div>
                  <Label htmlFor="postcode" className="text-sm font-semibold text-slate-800 mb-2 block">
                    Postcode (optioneel)
                  </Label>
                  <Input
                    id="postcode"
                    type="text"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    placeholder="bv. 1011 AB"
                    className="h-12"
                    data-testid="compare-postcode-input"
                  />
                  <p className="mt-1.5 text-xs text-slate-500">Voor regio-specifieke tarieven</p>
                </div>
              </div>

              {/* Solar */}
              <div className="mt-6">
                <Label className="text-sm font-semibold text-slate-800 mb-2 block">Heb je zonnepanelen?</Label>
                <div className="grid grid-cols-2 gap-2 max-w-sm">
                  <button
                    type="button"
                    onClick={() => setHasSolar(false)}
                    className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition-all ${
                      !hasSolar ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid="compare-solar-no"
                  >
                    Nee
                  </button>
                  <button
                    type="button"
                    onClick={() => setHasSolar(true)}
                    className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition-all ${
                      hasSolar ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid="compare-solar-yes"
                  >
                    Ja, ik heb zonnepanelen
                  </button>
                </div>
              </div>

              {/* Contract type */}
              <div className="mt-6">
                <Label className="text-sm font-semibold text-slate-800 mb-2 block">
                  Voorkeur contractvorm <span className="font-normal text-slate-500">(optioneel)</span>
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "vast", label: "Vast" },
                    { id: "variabel", label: "Variabel" },
                    { id: "dynamisch", label: "Dynamisch" },
                  ].map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setContractType(contractType === c.id ? "" : c.id)}
                      className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition-all ${
                        contractType === c.id
                          ? "bg-blue-700 text-white border-blue-700"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`compare-contract-${c.id}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority */}
              <div className="mt-6">
                <Label className="text-sm font-semibold text-slate-800 mb-2 block">
                  Wat is voor jou het belangrijkst?
                </Label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPriority(priority === p.id ? "" : p.id)}
                      className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                        priority === p.id
                          ? "bg-blue-700 text-white border-blue-700"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`compare-priority-${p.id}`}
                    >
                      <div className="font-semibold text-sm">{p.label}</div>
                      <div className={`text-xs mt-0.5 ${priority === p.id ? "text-blue-100" : "text-slate-500"}`}>
                        {p.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-8 w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-xl px-6 py-4 shadow-[0_8px_24px_-12px_rgba(29,78,216,0.5)] transition-all hover:-translate-y-0.5 disabled:opacity-50"
                data-testid="compare-submit-btn"
              >
                {loading ? "Even kijken..." : "Toon de beste aanbieders →"}
              </button>

              <div className="mt-5 flex flex-wrap justify-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                  100% Anoniem
                </span>
                <span>·</span>
                <span>Geen account nodig</span>
                <span>·</span>
                <span>9 ACM-erkende leveranciers</span>
              </div>
            </form>
          </section>
        )}

        {step === "result" && result && (
          <section data-testid="compare-result-section">
            <div className="mb-6">
              <button
                onClick={() => setStep("form")}
                className="text-sm text-slate-600 hover:text-blue-700 flex items-center gap-1.5 transition-colors mb-4"
                data-testid="compare-edit-btn"
              >
                <ArrowLeft className="h-4 w-4" /> Aanpassen
              </button>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">
                Onze top aanbieders voor jou
              </h1>
              <p className="mt-2 text-slate-600">
                {kwh && `Op basis van ${kwh} kWh verbruik`}
                {hasSolar && " · met zonnepanelen"}
                {priority && ` · prioriteit ${PRIORITIES.find((p) => p.id === priority)?.label.toLowerCase()}`}
              </p>
            </div>

            <ProviderGrid
              top={result.top}
              all={result.all}
              onProviderClick={handleProviderClick}
            />
          </section>
        )}
      </main>
    </div>
  );
}
